/**
 * Analytics GraphQL Fragments
 *
 * Reusable fragments for Analytics primitive queries and mutations.
 * 46 fields organized by category for business intelligence operations.
 *
 * @see graphql_gateway/src/analytics_api/analytics_model.rs - Backend model
 */

import { gql } from '@apollo/client';

/**
 * Complete Analytics fields fragment (46 fields)
 *
 * Used by all queries and mutations that return Analytics data.
 */
export const ANALYTICS_FIELDS = gql`
  fragment AnalyticsFields on Analytics {
    # Identity (4 fields - tenantId is graphql(skip))
    analyticsId
    analyticsName
    analyticsCode
    description

    # Formula Interaction (6 fields)
    sourcePrimitiveType
    sourcePrimitiveIds
    workflowInstanceIds
    queueIds
    actorPoolIds
    contractId

    # Metric Configuration (12 fields)
    metricType
    metricName
    metricUnit
    metricCategory
    aggregationType
    aggregationWindowMinutes
    percentileTargets
    sampleRate
    retentionDays
    rollupRetentionDays
    maxCardinality
    enableRealTime

    # Aggregation Rules (8 fields)
    groupByDimensions
    filterConditionsJson
    excludePatterns
    derivedMetricsJson
    baselineCalculation
    anomalyDetectionConfigJson
    nullHandling
    outlierTreatment

    # Current State (9 fields)
    currentValue
    previousValue
    trendDirection
    trendPercentage
    minValue
    maxValue
    averageValue
    standardDeviation
    lastCalculatedAt

    # Thresholds & Alerting (6 fields)
    warningThreshold
    criticalThreshold
    thresholdDirection
    alertOnThresholdBreach
    notificationActorIds
    cooldownMinutes

    # Lifecycle (4 fields - createdByUserId is graphql(skip), use resolver)
    createdAt
    updatedAt
    enabled
    lastError
    metadata

    # Complex Object Resolvers (2 fields)
    compressedId
    createdByActor {
      id
      displayName
      role
    }
  }
`;

/**
 * Analytics connection fields (pagination)
 */
export const ANALYTICS_CONNECTION_FIELDS = gql`
  ${ANALYTICS_FIELDS}
  fragment AnalyticsConnectionFields on AnalyticsConnection {
    nodes {
      ...AnalyticsFields
    }
    totalCount
    hasNextPage
    endCursor
  }
`;

/**
 * Analytics dashboard fields (summary view)
 */
export const ANALYTICS_DASHBOARD_FIELDS = gql`
  ${ANALYTICS_FIELDS}
  fragment AnalyticsDashboardFields on AnalyticsDashboard {
    tenantId
    lastUpdated
    performanceMetrics {
      ...AnalyticsFields
    }
    adoptionMetrics {
      ...AnalyticsFields
    }
    businessMetrics {
      ...AnalyticsFields
    }
    systemMetrics {
      ...AnalyticsFields
    }
    complianceMetrics {
      ...AnalyticsFields
    }
    alertsTriggered
    trendsPositive
    trendsNegative
  }
`;

/**
 * Time series data point fields
 */
export const TIME_SERIES_DATA_POINT_FIELDS = gql`
  fragment TimeSeriesDataPointFields on TimeSeriesDataPoint {
    timestamp
    value
    count
    min
    max
    avg
    stdDev
  }
`;
